// src/oura/hooks/useRefreshJob.ts
//
// One refresh, run once, and impossible to lose (#3904, epic #3903).
//
// This is the AI Readability refresh (#3831) with the card taken out of it.
// Nothing here is new behaviour; it is the behaviour that took four rounds of
// review to get right, written down once so the next four cards inherit it
// instead of each re-deriving it a little differently.
//
// THE IN-FLIGHT STATE IS STICKY. That is the whole point of the hook. A
// staffer who clicks again — and they will, because that is what people do when
// something is taking a while — must not be able to clear the spinner, restart
// the job, or fire a second request. Every early return below is there to make
// losing it impossible rather than merely unlikely.

import { useCallback, useEffect, useRef, useState } from "react";
import { getStageRerun, startStageRerun, type AeoRerunKind } from "@service/aeo";
import { explainRefreshFailure, refreshStorageKey, type RefreshJobSpec } from "../refreshJobs";

export type RefreshNote = { tone: "info" | "warn"; text: string };

/** What starting a refresh came back with. `absent` is the 404/405 that says
 *  this deployment has no such route, and it removes the control entirely. */
export type RefreshStart<R> =
    | { ok: true; jobId?: string | null; mode?: string | null; result?: R | null }
    | { ok: false; kind: "busy" | "forbidden" | "error"; message: string }
    // `absent` carries no message on purpose: there is nothing to say to a
    // staffer about a route that is not deployed. The control simply goes.
    | { ok: false; kind: "absent"; message?: string };

/** One poll. `"gone"` is an id the backend no longer knows; `null` is a dropped
 *  request, which is not a failed job — the loop keeps asking. */
export type RefreshPoll<R> =
    | { status: "queued" | "running" | "done" | "failed"; mode?: string | null; error?: string | null; result?: R | null }
    | "gone"
    | null;

export interface RefreshJob<R> {
    /** True from the press until a terminal outcome. Never cleared by a second
     *  press, by a 409, or by polling giving up. */
    running: boolean;
    mode: string | null;
    note: RefreshNote | null;
    /** The route is not deployed here. Render no control at all. */
    absent: boolean;
    result: R | null;
    confirmOpen: boolean;
    /** Open the confirm dialog. A no-op while a job runs. */
    ask: () => void;
    cancel: () => void;
    /** Start it. A no-op while a job runs. */
    run: () => Promise<void>;
}

export function useRefreshJob<R>({
    storageKey,
    start,
    poll,
    onSettled,
    pollMs = 2_000,
    pollLimit = 300,
}: {
    /** Per business AND per job family, so two families cannot resume each
     *  other's id. */
    storageKey: string;
    start: () => Promise<RefreshStart<R>>;
    poll: (jobId: string) => Promise<RefreshPoll<R>>;
    /** Re-read the page once the job lands, so no card lives on newer data than
     *  the rest of the screen. */
    onSettled?: () => void | Promise<void>;
    pollMs?: number;
    pollLimit?: number;
}): RefreshJob<R> {
    const [running, setRunning] = useState(false);
    const [mode, setMode] = useState<string | null>(null);
    const [note, setNote] = useState<RefreshNote | null>(null);
    const [absent, setAbsent] = useState(false);
    const [result, setResult] = useState<R | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const aliveRef = useRef(true);
    // The guard that makes a second click harmless. State would be a frame
    // behind a fast double-press; a ref is not.
    const runningRef = useRef(false);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const setRunningBoth = useCallback((v: boolean) => {
        runningRef.current = v;
        setRunning(v);
    }, []);

    const remember = useCallback((jobId: string | null) => {
        try {
            if (jobId) window.localStorage.setItem(storageKey, jobId);
            else window.localStorage.removeItem(storageKey);
        } catch {
            // Private windows and blocked storage both throw. The refresh still
            // works; it just stops surviving a reload.
        }
    }, [storageKey]);

    const follow = useCallback(async (jobId: string) => {
        for (let i = 0; i < pollLimit; i++) {
            await new Promise((r) => setTimeout(r, pollMs));
            if (!aliveRef.current) return;
            const job = await poll(jobId);
            // A dropped poll is not a failed job: keep asking.
            if (!job) continue;
            if (job === "gone") {
                // The id expired or the backend forgot it. Nothing to follow,
                // and nothing to report as a failure either. The note goes with
                // it: "picking up the refresh that was already running" is
                // false the moment there is nothing to pick up.
                remember(null);
                setRunningBoth(false);
                setMode(null);
                setNote(null);
                return;
            }
            if (job.mode) setMode(job.mode);
            if (job.status === "failed") {
                remember(null);
                setRunningBoth(false);
                setMode(null);
                setNote({ tone: "warn", text: job.error || "The refresh did not finish. Nothing was changed." });
                return;
            }
            if (job.status === "done") {
                remember(null);
                setRunningBoth(false);
                setMode(null);
                if (job.result) setResult(job.result);
                void onSettled?.();
                return;
            }
        }
        if (!aliveRef.current) return;
        // NO SILENT TIMEOUT. We stopped watching; the backend did not stop
        // working. Returning to idle here would read as "nothing happened" over
        // a job that is still going, so the state stays in flight and the id
        // stays stored, which is what lets a reload pick it back up.
        setNote({ tone: "info", text: "Still running. Reload in a minute to pick it up again." });
    }, [poll, pollLimit, pollMs, remember, setRunningBoth, onSettled]);

    // Re-attach on mount to whatever this browser last started here.
    useEffect(() => {
        let stored: string | null = null;
        try { stored = window.localStorage.getItem(storageKey); } catch { stored = null; }
        if (!stored) return;
        setRunningBoth(true);
        setNote({ tone: "info", text: "Picking up the refresh that was already running." });
        void follow(stored);
        // Keyed on the job, and run once for it: the poll loop owns the rest of
        // its own life.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    const ask = useCallback(() => {
        if (runningRef.current) return;
        setConfirmOpen(true);
    }, []);

    const cancel = useCallback(() => setConfirmOpen(false), []);

    const run = useCallback(async () => {
        if (runningRef.current) return;
        setConfirmOpen(false);
        setNote(null);
        // Optimistic, and deliberately BEFORE the await: the press has to take
        // effect on the frame it happened, or a slow POST leaves a window where
        // a second click still gets through.
        setRunningBoth(true);
        const started = await start();
        if (!aliveRef.current) return;
        if (!started.ok) {
            if (started.kind === "absent") {
                setRunningBoth(false);
                setAbsent(true);
                return;
            }
            // A 409 means work IS happening, just not ours: an assessment is
            // already reading this site. Our job never started, so the
            // optimistic flag above is rolled back — and it can only ever be
            // the optimistic one, because `run()` refuses outright while a job
            // of ours is in flight. That guard is what makes it impossible for
            // a 409 to end a spinner that was already spinning.
            if (started.kind === "busy") {
                setNote({ tone: "warn", text: started.message });
                setRunningBoth(false);
                return;
            }
            setRunningBoth(false);
            setNote({ tone: "warn", text: started.message });
            return;
        }
        // A recompute can already be done by the time the POST answers — the
        // block rides back with it, so polling for an answer we are holding
        // would put two seconds between the click and the number for nothing.
        if (started.result) {
            remember(null);
            setRunningBoth(false);
            setMode(null);
            setResult(started.result);
            void onSettled?.();
            return;
        }
        setMode(started.mode ?? null);
        if (!started.jobId) {
            // Started, but nothing to follow. Treat it as landed rather than
            // spinning forever on an id that does not exist.
            setRunningBoth(false);
            void onSettled?.();
            return;
        }
        remember(started.jobId);
        await follow(started.jobId);
    }, [start, follow, remember, setRunningBoth, onSettled]);

    return { running, mode, note, absent, result, confirmOpen, ask, cancel, run };
}

/** A stage re-run, with what the backend said it was and what it cost. */
export type StageRefreshJob = RefreshJob<never> & {
    kind: AeoRerunKind | null;
    cost: number | null;
    /** `results[stage][block]` as each stage lands, straight from the poll. */
    blocks: Record<string, Record<string, unknown>> | null;
    /** The last run failed because the source is not connected or authorised:
     *  the fix is on the Connectors page, not another press. */
    needsConnector: boolean;
};

/**
 * One card's refresh, bound to a stage re-run (epic #3903,
 * ourteam-backend#5059).
 *
 * Every screen wires its cards through this rather than through `useRefreshJob`
 * directly, so the stage list, the dialog copy and the storage key all come
 * from one spec and cannot drift between screens.
 *
 * THE COST IS REPORTED, NOT PREDICTED. The backend states `kind` and
 * `estimatedCostUsd` when the job starts; there is no dry-run, so the dialog
 * says what this KIND of refresh does and the note says what this RUN actually
 * cost. A figure guessed in the frontend before asking would be the one number
 * on the screen nobody measured.
 */
export function useStageRefresh({
    spec,
    assessmentId,
    businessId,
    onSettled,
}: {
    spec: RefreshJobSpec;
    /** Undefined on a fixture business or before the detail arrives — the
     *  control does not render without it. */
    assessmentId?: string;
    businessId: string;
    onSettled?: () => void | Promise<void>;
}): StageRefreshJob {
    const [kind, setKind] = useState<AeoRerunKind | null>(null);
    const [cost, setCost] = useState<number | null>(null);
    const [blocks, setBlocks] = useState<Record<string, Record<string, unknown>> | null>(null);
    const [needsConnector, setNeedsConnector] = useState(false);

    const job = useRefreshJob<never>({
        storageKey: refreshStorageKey(spec, businessId),
        start: async () => {
            if (!assessmentId) return { ok: false, kind: "absent" };
            setNeedsConnector(false);
            const r = await startStageRerun(assessmentId, spec.stages);
            if (!r.ok) return r;
            setKind(r.kind ?? null);
            setCost(typeof r.estimatedCostUsd === "number" ? r.estimatedCostUsd : null);
            return { ok: true, jobId: r.jobId, mode: spec.key };
        },
        poll: async (jobId) => {
            if (!assessmentId) return "gone";
            const j = await getStageRerun(assessmentId, jobId);
            if (j === "gone" || !j) return j;
            // Applied on every poll, not only at the end: a crawl that has
            // finished while `finalize` still runs already has numbers the card
            // can show, and holding them back would leave it looking empty over
            // work that is done.
            if (j.results) setBlocks(j.results);
            if (j.kind) setKind(j.kind);
            if (typeof j.estimatedCostUsd === "number") setCost(j.estimatedCostUsd);
            if (j.status === "failed") {
                const why = explainRefreshFailure(spec, j.error);
                setNeedsConnector(!!why?.needsConnector);
                return { status: j.status, mode: spec.key, error: why?.text ?? j.error };
            }
            return { status: j.status, mode: spec.key, error: j.error };
        },
        onSettled,
    });

    return { ...job, kind, cost, blocks, needsConnector };
}
